import { PlusOutlined } from "@ant-design/icons";
import { Button, Spin } from "antd";
import React, { useEffect, useState, Suspense } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BreadcrumbHeader } from "~/components/layout/breadcrumbHeader.jsx";
import { MainLayout } from "~/components/layout/layout.jsx";
import { ReservationForm } from "~/components/reservation/ReservationForm.jsx";
import { convertToCalendarEvent } from "~/components/reservation/helpers.js";
import { $t } from "~/i18n.js";
import { api } from "~/lib/api.js";
import { selectElementByID, updateIfNeeded } from "~/lib/helpers.js";
import { daysActions } from "~/redux/actions.js";
import { getBaseData } from "~/redux/flights/flights.actions.js";
import { getMaterialData } from "~/redux/materiaal/materiaal.actions.js";

const ReservationCalendar = React.lazy(() =>
  import("~/components/reservation/ReservationCalendar.jsx"),
);

export const ReservationPage = ({ history, match }) => {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeReservationID, setActiveReservationID] = useState(null);

  const profile = useSelector((state) => state.persist.profile);
  const vliegers = useSelector((state) => state.flights.vliegers);
  const allDays = useSelector((state) => state.days.allDays);
  const dispatch = useDispatch();

  const openReservation = (id, reservationsList = reservations) => {
    const reservationId = parseInt(id);
    const reservation = reservationsList.find((r) => r.id === reservationId);
    if (reservation) {
      setActiveReservationID(reservationId);
    }
  };

  const getReservations = async () => {
    setLoading(true);
    const res = await api.get("reservation.json");
    if (res) {
      setReservations(res);
      // Check if we need to open a reservation from URL parameter
      if (match?.params?.id) {
        openReservation(match.params.id, res);
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    getReservations();
    // Fetch days data if not loaded
    updateIfNeeded(null, () => dispatch(daysActions.getAllDays()));
  }, [dispatch]);

  // Handle URL parameter changes after reservations are already loaded
  useEffect(() => {
    if (match?.params?.id && reservations.length > 0) {
      openReservation(match.params.id);
    }
  }, [match?.params?.id]);

  const { lastUpdatedBaseData } = useSelector((state) => state.flights);
  useEffect(() => {
    updateIfNeeded(lastUpdatedBaseData, () => dispatch(getBaseData()));
  }, [lastUpdatedBaseData, dispatch]);
  const { material } = useSelector((state) => state.materiaal);
  useEffect(() => {
    if (material === null) {
      dispatch(getMaterialData());
    }
  }, [material, dispatch]);

  const saveReservation = async (reservationData) => {
    const method = reservationData.id ? "patch" : "post";
    const res = await api[method]("reservation.json", reservationData);
    if (res) {
      await getReservations();
      return res;
    }
  };

  const deleteReservation = async (reservationData) => {
    const { response, data } = await api.delete(
      "reservation.json",
      reservationData,
    );
    if (data) {
      await getReservations();
      return data;
    }
  };

  // Determine active reservation
  let activeReservation = null;
  if (activeReservationID === -1) {
    // New reservation
    activeReservation = {
      id: null,
      material_id: null,
      date_from: null,
      date_to: null,
      user_remark: "",
      status: "open",
    };
  } else if (activeReservationID !== null) {
    // Edit existing
    activeReservation = selectElementByID(
      reservations,
      "id",
      activeReservationID,
    );
  }

  // Permission checks
  const isAdmin = profile?.app_permissions?.includes("wijzig_reservering");
  const isOwner = activeReservation?.user?.id === profile?.id;

  const handleEventClick = (info) => {
    setActiveReservationID(Number(info.event.id));
  };

  const handleDateClick = (info) => {
    // Check if clicked date has a registered day
    const clickedDate = info.dateStr;
    const day = allDays?.find((d) => d.datum === clickedDate);
    if (day) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const clickedDateObj = new Date(clickedDate);
      clickedDateObj.setHours(0, 0, 0, 0);

      if (clickedDateObj < today) {
        history.push(`/start/${day.dag_id}`);
      } else {
        history.push(`/register/${day.dag_id}`);
      }
    }
  };

  return (
    <>
      {activeReservation && (
        <ReservationForm
          visible={activeReservation !== null}
          closeModal={() => setActiveReservationID(null)}
          saveReservation={saveReservation}
          deleteReservation={deleteReservation}
          activeReservation={activeReservation}
          isAdmin={isAdmin}
          isOwner={isOwner}
          vliegers={vliegers}
          profile={profile}
        />
      )}

      <MainLayout history={history}>
        <BreadcrumbHeader
          breadcrumbs={[$t("Reserveren")]}
          buttons={[
            <Button
              key="sync"
              size="small"
              type="default"
              onClick={() => getReservations()}
              style={{ marginRight: 6 }}
            >
              {$t("Sync")}
            </Button>,
            <Button
              key="add"
              size="small"
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setActiveReservationID(-1)}
            >
              {$t("Voeg toe")}
            </Button>,
          ]}
        />

        <Suspense
          fallback={
            <div style={{ textAlign: "center", padding: 50 }}>
              <Spin size="large" />
            </div>
          }
        >
          <ReservationCalendar
            events={reservations.map(convertToCalendarEvent)}
            onEventClick={handleEventClick}
            onDateClick={handleDateClick}
            loading={loading}
            allDays={allDays}
          />
        </Suspense>
      </MainLayout>
    </>
  );
};
